import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { HotTable } from '@handsontable/react-wrapper'
import { registerAllModules } from 'handsontable/registry'
import 'handsontable/styles/handsontable.css'
import 'handsontable/styles/ht-theme-horizon.css'
import { cable } from '../../cable.js'
import { t } from '../../i18n.js'
import { isoToDDMMYYYY as formatDate } from '../utils/dateFormat.js'
import { HOT_LICENSE_KEY } from '../utils/handsontableLicense.js'
import { sanitizeHandsontableHtml } from '../utils/handsontableSanitizer.js'

registerAllModules()

const REALTIME_DEBOUNCE = 1500

// Column groups mirror devdoc 2 §4 (General 8 / Dates 2 / Rates 6 / Uplift 5).
// `label` is now a translation key under components.indexation_events_table.columns.
const COLUMNS = [
  { key: 'tenant_name',                group: 'tenant',  type: 'text' },
  { key: 'indexation_name',            group: 'general', type: 'text' },
  { key: 'indexation_type',            group: 'general', type: 'profile_type' },
  { key: 'lease_type',                 group: 'general', type: 'text' },
  { key: 'mla_instance',               group: 'general', type: 'number' },
  { key: 'payment_frequency',          group: 'general', type: 'payment_frequency' },
  { key: 'lifecycle_key',              group: 'general', type: 'text' },
  { key: 'indexation_date',            group: 'dates',   type: 'date' },
  { key: 'cash_flow_effective_date',   group: 'dates',   type: 'date' },
  { key: 'applied_lease_end_date',     group: 'dates',   type: 'date' },
  { key: 'index_date_year_inflation',  group: 'rates',   type: 'rate' },
  { key: 'min_rate',                   group: 'rates',   type: 'rate' },
  { key: 'max_rate',                   group: 'rates',   type: 'rate' },
  { key: 'hurdle_rate',                group: 'rates',   type: 'rate' },
  { key: 'weighted_raw_inflation',     group: 'rates',   type: 'rate' },
  { key: 'applied_inflation',          group: 'rates',   type: 'rate' },
  { key: 'rule_result',                group: 'rates',   type: 'text' },
  { key: 'base_rent',                  group: 'uplift',  type: 'money' },
  { key: 'uplift',                     group: 'uplift',  type: 'money' },
  { key: 'periodic_uplift',            group: 'uplift',  type: 'money' },
  { key: 'cumulative_uplift',          group: 'uplift',  type: 'money' },
  { key: 'cumulative_periodic_uplift', group: 'uplift',  type: 'money' }
]

const RIGHT_ALIGNED_TYPES = new Set([ 'money', 'rate', 'number' ])

function formatMoney(value) {
  if (value === null || value === undefined || value === '') return ''
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatRate(value) {
  if (value === null || value === undefined || value === '') return ''
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })
}

function formatCell(column, row, translations) {
  const raw = row[column.key]
  if (raw === null || raw === undefined) return ''
  switch (column.type) {
    case 'date':              return formatDate(raw)
    case 'money':             return formatMoney(raw)
    case 'rate':              return formatRate(raw)
    case 'profile_type':      return t(`types.${raw}`, translations) || raw
    case 'payment_frequency': return t(`schedules.${raw}`, translations) || raw
    case 'number':            return raw === '' ? '' : String(raw)
    default:                  return String(raw)
  }
}

function buildGroupHeaderRow(columns, translations) {
  const cells = []
  const merges = []
  let i = 0
  while (i < columns.length) {
    let j = i
    while (j < columns.length && columns[j].group === columns[i].group) j += 1
    cells.push(t(`groups.${columns[i].group}`, translations))
    for (let k = i + 1; k < j; k += 1) cells.push('')
    if (j - i > 1) merges.push({ row: 0, col: i, rowspan: 1, colspan: j - i })
    i = j
  }
  return { row: cells, merges: merges }
}

const ALL_TENANTS = '__ALL__'

export default function IndexationEventsTable({ valuationId, dataUrl, currency, translations }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [rows, setRows] = useState([])
  // Swarm2 D-M1: simple tenant filter — analysts often want to inspect one
  // tenant's indexation timeline in isolation. Filtering is client-side only,
  // operating on the already-fetched rows.
  const [tenantFilter, setTenantFilter] = useState(ALL_TENANTS)
  const fetchRef = useRef(null)

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    setError(null)
    try {
      const res = await fetch(dataUrl, { headers: { Accept: 'application/json' } })
      const json = await res.json()
      if (!res.ok || !json.success) {
        const code = json.error || t('request_failed', translations, { status: res.status })
        throw new Error(code === 'access_denied' ? t('access_denied', translations) : code)
      }
      setRows((json.data && json.data.rows) || [])
    } catch (err) {
      setError(err.message || t('load_error', translations))
    } finally {
      if (!silent) setLoading(false)
    }
  }, [dataUrl, translations])

  useEffect(() => { fetchRef.current = fetchData }, [fetchData])
  useEffect(() => { fetchData() }, [fetchData])

  // Live-recalc when ANY valuation input changes (Tenants, MLA, GrowthRates,
  // IndexationProfiles) via the shared pulse channel.
  useEffect(() => {
    if (!valuationId) return undefined
    let debounceTimer = null

    const subscription = cable.subscriptions.create(
      { channel: 'ValuationChannel', valuation_id: valuationId },
      {
        received() {
          if (debounceTimer) clearTimeout(debounceTimer)
          debounceTimer = setTimeout(() => { fetchRef.current?.(true) }, REALTIME_DEBOUNCE)
        }
      }
    )

    return () => {
      subscription.unsubscribe()
      if (debounceTimer) clearTimeout(debounceTimer)
    }
  }, [valuationId])

  const tenantOptions = useMemo(() => {
    const seen = new Set()
    const list = []
    rows.forEach(r => {
      const name = r.tenant_name
      if (name && !seen.has(name)) { seen.add(name); list.push(name) }
    })
    return list.sort((a, b) => a.localeCompare(b))
  }, [rows])

  const filteredRows = useMemo(() => {
    if (tenantFilter === ALL_TENANTS) return rows
    return rows.filter(r => r.tenant_name === tenantFilter)
  }, [rows, tenantFilter])

  const tableData = useMemo(() => {
    const header = buildGroupHeaderRow(COLUMNS, translations)
    const rowData = filteredRows.map(r => COLUMNS.map(c => formatCell(c, r, translations)))
    return { header: header, body: rowData }
  }, [filteredRows, translations])

  const mergeCells = useMemo(() => tableData.header.merges, [tableData])
  const data = useMemo(() => [ tableData.header.row, ...tableData.body ], [tableData])

  // Swarm D17: append the valuation currency to money column labels so
  // analysts can tell £/€/$ apart at a glance.
  const colHeaders = useMemo(() => COLUMNS.map(c => {
    const label = t(`columns.${c.key}`, translations)
    if (c.type === 'money' && currency) {
      return t('currency_suffix', translations, { label, currency })
    }
    return label
  }), [currency, translations])

  const columns = useMemo(() => COLUMNS.map(c => ({
    readOnly: true,
    className: RIGHT_ALIGNED_TYPES.has(c.type) ? 'htRight' : 'htLeft'
  })), [])

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('loading', translations)}</span>
        </div>
      </div>
    )
  }

  if (error) {
    return <div className="alert alert-danger mb-0">{error}</div>
  }

  if (!rows.length) {
    return <div className="p-4 text-muted">{t('empty_state', translations)}</div>
  }

  return (
    <div>
      {tenantOptions.length > 1 && (
        <div className="d-flex align-items-center gap-2 mb-2">
          <label className="form-label mb-0 small text-muted" htmlFor="indexation-events-tenant-filter">{t('filter.label', translations)}</label>
          <select
            id="indexation-events-tenant-filter"
            className="form-select form-select-sm w-auto"
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
          >
            <option value={ALL_TENANTS}>{t('filter.all', translations, { count: rows.length })}</option>
            {tenantOptions.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          {tenantFilter !== ALL_TENANTS && (
            <span className="small text-muted">{t('filter.status', translations, { filtered: filteredRows.length, total: rows.length })}</span>
          )}
        </div>
      )}
      <HotTable
        themeName="ht-theme-horizon"
        data={data}
        colHeaders={colHeaders}
        columns={columns}
        mergeCells={mergeCells}
        fixedRowsTop={1}
        fixedColumnsStart={1}
        width="100%"
        height={520}
        stretchH="none"
        readOnly
        manualColumnResize
        licenseKey={HOT_LICENSE_KEY}
        sanitizer={sanitizeHandsontableHtml}
      />
    </div>
  )
}
